/**
 * The Meta ads panel: the shell, its buttons — the connection's three, and the
 * way to the composer slice 42 added — and the request that fills it.
 *
 * ## WHAT IS HERE AND WHAT IS NEXT DOOR
 *
 * This file holds everything that talks to the API — the load, the account
 * read (slice 41), the Test, the disconnect's parent. It therefore reaches
 * `lib/config.ts` and its `import.meta.env`, which evaluates only inside Vite,
 * so **no verify script can render this module at all**.
 *
 * Everything worth asserting about the screen is consequently in two files that
 * import no client and which the suite renders directly:
 * `MetaAdsAccountState.tsx` for the connection — the three permission states,
 * the two meanings of a null expiry, the stub notice — and
 * `MetaAdsAccountRead.tsx` for what the account holds — money at the last
 * moment, reach never summed, a null cap as no cap, and a refusal that is never
 * drawn as an empty account. Same split `WhatsAppCard` and `WhatsAppSendState`
 * keep, for the same reason.
 *
 * ## WHAT IS DRAWN FROM `integration` AND WHAT FROM THE SESSION
 *
 * The card renders the **row** — what Meta last confirmed. A Test in this
 * session adds one thing the row cannot carry: which transport answered. A
 * green taken through the stub is not evidence about a real ad account, and the
 * next three slices spend money through whatever this said was connected, so it
 * is rendered rather than assumed.
 */
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Kicker, Spinner, buttonVariants } from '@wend/ui';
import { KeyRound, Megaphone, RotateCw } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { ErrorState } from '../../components/ErrorState';
import { META_ADS_WINDOW_DEFAULT_DAYS } from '@wend/shared';

import {
  checkMetaAds,
  fetchMetaAdsAccountRead,
  fetchMetaAdsIntegration,
  type MetaAdsCheckResponse,
  type MetaAdsIntegration,
} from '../../lib/api/meta-ads';
import { useAsyncData } from '../../lib/use-async-data';
import {
  META_ADS_ATTENTION_TONE,
  metaAdsTabStatus,
  type MetaAdsTabStatus,
} from './meta-ads-reading';
import { MetaAdsAccountRead } from './MetaAdsAccountRead';
import { MetaAdsAccountState, type MetaAdsSessionCheck } from './MetaAdsAccountState';
import { MetaAdsConnectForm } from './MetaAdsConnectForm';
import { MetaAdsDisconnect } from './MetaAdsDisconnect';
import { RequestFailureAlert, requestFailure, type RequestFailure } from './request-failure';

export interface MetaAdsTabPanelProps {
  /**
   * Called whenever this panel learns a status the rail has not heard yet.
   *
   * Never during the first load and never after a request that failed: "nothing
   * connected" is a claim, and a request that did not answer supports no claim
   * at all.
   */
  readonly onStatusChange?: (status: MetaAdsTabStatus) => void;
}

export function MetaAdsTabPanel({ onStatusChange }: MetaAdsTabPanelProps) {
  /*
   * A wrapper object rather than `MetaAdsIntegration | null`, so "not loaded
   * yet" and "loaded, nothing connected" stay different values. Both are null
   * inside, and conflating them shows the connect form to somebody whose
   * request actually failed.
   */
  const { data, error, loadingFirst, refreshing, reload } = useAsyncData(
    async (signal) => ({ integration: await fetchMetaAdsIntegration(signal) }),
    [],
  );

  const [local, setLocal] = useState<{ integration: MetaAdsIntegration } | null>(null);
  const [sessionCheck, setSessionCheck] = useState<MetaAdsSessionCheck | null>(null);
  const [replacing, setReplacing] = useState(false);

  const integration = local?.integration ?? data?.integration ?? null;
  const known = local !== null || data !== null;
  const status = metaAdsTabStatus(integration);

  useStatusReport(known ? status : null, onStatusChange);

  function onAnswered(response: MetaAdsCheckResponse): void {
    setLocal({ integration: response.integration });
    setSessionCheck({ outcome: response.outcome });
    setReplacing(false);
  }

  if (!known) {
    return (
      <Card>
        {loadingFirst ? (
          <CardContent className="flex items-center gap-3 px-6 py-10 text-[0.92rem] text-muted">
            <Spinner size="md" label={null} />
            <span role="status">Loading your ad account…</span>
          </CardContent>
        ) : (
          <ErrorState error={error} title="Could not load your ad account" onRetry={reload} />
        )}
      </Card>
    );
  }

  return (
    <Card data-role="meta-ads-card">
      <CardHeader>
        <div className="flex flex-wrap items-center gap-3">
          <span className="grid size-8 shrink-0 place-items-center rounded-base border border-line bg-white/[0.04] text-muted">
            <Megaphone aria-hidden="true" className="size-4" />
          </span>
          <Kicker as="span">
            Facebook · Instagram · WhatsApp · Meta
          </Kicker>
          {/* The rail's mark and this badge are the same value. */}
          <Badge tone={META_ADS_ATTENTION_TONE[status.attention]}>{status.label}</Badge>
          {refreshing && <Spinner size="sm" label="Refreshing" />}
        </div>
        {/* The account, not the ads — "Meta ads" is Reach's list since slice 65. */}
        <CardTitle>Meta ad account</CardTitle>
      </CardHeader>

      <CardContent className="flex flex-col gap-6">
        {integration === null || replacing ? (
          <MetaAdsConnectForm
            existing={integration}
            onConnected={onAnswered}
            {...(replacing
              ? {
                  onCancel: () => {
                    setReplacing(false);
                  },
                }
              : {})}
          />
        ) : (
          <div className="flex flex-col gap-6">
            <MetaAdsAccountState integration={integration} sessionCheck={sessionCheck} />

            <MetaAdsReadPanel integration={integration} />

            <div className="flex flex-wrap items-center gap-3">
              {/* Slice 42: this is the way in to composing from the
                  connection it depends on — to the composer itself, by the
                  Reach page's own name for it, "New ad": one action, one name.
                  The composer is under Reach since slice 65 split the ads from
                  the campaigns. Secondary: it must not outrank the buttons
                  that repair the connection. */}
              <Link to="/reach/ads/new" className={buttonVariants({ variant: 'secondary', size: 'sm' })}>
                <Megaphone aria-hidden="true" className="size-4" />
                New ad
              </Link>
              <TestButton onChecked={onAnswered} />
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => {
                  setReplacing(true);
                }}
              >
                <KeyRound aria-hidden="true" className="size-4" />
                Replace credentials
              </Button>
              <MetaAdsDisconnect
                integration={integration}
                onDisconnected={() => {
                  setLocal(null);
                  setSessionCheck(null);
                  reload();
                }}
              />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Slice 41 — what the account holds                                           */
/* -------------------------------------------------------------------------- */

/**
 * The account read, fetched here and drawn by `MetaAdsAccountRead`.
 *
 * Keyed on the integration's id and `lastVerifiedAt`, so a Test or a
 * replacement re-reads the account the moment Meta has confirmed something
 * new — and a read never outlives the credential it was taken with.
 *
 * A request that failed is an `ErrorState` with a retry; a read Meta refused is
 * a `failed` state drawn by the component, with Meta's repair. The two are
 * different failures and look different, for the reason the Test button keeps
 * them apart.
 */
function MetaAdsReadPanel({ integration }: { integration: MetaAdsIntegration }) {
  const { data, error, loadingFirst, reload } = useAsyncData(
    (signal) => fetchMetaAdsAccountRead(META_ADS_WINDOW_DEFAULT_DAYS, signal),
    [integration.id, integration.lastVerifiedAt],
  );

  if (data === null) {
    return loadingFirst ? (
      <div className="flex items-center gap-3 border-t border-line pt-5 text-[0.88rem] text-muted">
        <Spinner size="sm" label={null} />
        <span role="status">Reading your ad account…</span>
      </div>
    ) : (
      <ErrorState error={error} title="Could not read your ad account" onRetry={reload} />
    );
  }

  return <MetaAdsAccountRead state={data} />;
}

/* -------------------------------------------------------------------------- */
/* Test                                                                        */
/* -------------------------------------------------------------------------- */

function TestButton({ onChecked }: { onChecked: (response: MetaAdsCheckResponse) => void }) {
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<RequestFailure | null>(null);

  async function run(): Promise<void> {
    if (pending) return;
    setFailure(null);
    setPending(true);
    try {
      onChecked(await checkMetaAds());
    } catch (error) {
      /* The REQUEST failed — Meta was never asked. A check Meta refused comes
         back as a 200 with `ok: false` and is rendered on the card, not here. */
      setFailure(requestFailure(error));
    } finally {
      setPending(false);
    }
  }

  return (
    <>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        loading={pending}
        onClick={() => {
          void run();
        }}
      >
        <RotateCw aria-hidden="true" className="size-4" />
        {pending ? 'Asking Meta' : 'Test'}
      </Button>
      {failure !== null && <RequestFailureAlert failure={failure} />}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Reporting upward                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Reports a status upward, once per distinct status.
 *
 * No dependency array, the same shape `WhatsAppTabPanel`'s reporter uses and
 * for the same reason: it early-returns the moment there is nothing new, and
 * what it compares is a value the caller recreates on every render. An array
 * over `status` would fire every render; one over `onStatusChange` would go
 * stale the moment the shell passed an inline arrow.
 *
 * `null` means "this panel does not know yet", and nothing is reported for it.
 */
function useStatusReport(
  status: MetaAdsTabStatus | null,
  onStatusChange: ((status: MetaAdsTabStatus) => void) | undefined,
): void {
  const last = useRef<MetaAdsTabStatus | null>(null);

  useEffect(() => {
    if (status === null || onStatusChange === undefined) return;
    if (last.current?.label === status.label && last.current.attention === status.attention) return;
    last.current = status;
    onStatusChange(status);
  });
}
